import { useState } from 'react';
import type { GridColumn } from '@hcc/types';
import { GRID_CELL_TYPES } from '@hcc/types';
import { Button } from '../../ui/Button';
import { OptionsEditor } from './OptionsEditor';
import { Plus, X, ChevronDown, ChevronRight } from 'lucide-react';

interface GridColumnsEditorProps {
  columns: GridColumn[];
  onChange: (columns: GridColumn[]) => void;
}

const cellClass = 'rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm';

/** The typed-columns half of the grid builder. */
export function GridColumnsEditor({ columns, onChange }: GridColumnsEditorProps) {
  const [openCol, setOpenCol] = useState<number | null>(null);

  return (
    <div>
      <div className="mb-1.5 text-xs font-medium text-fg-muted">Columns</div>
      <div className="space-y-2">
        {columns.map((c, i) => (
          <div key={i} className="rounded-token-sm border border-line/10 bg-surface-2 p-2">
            <div className="flex items-center gap-2">
              <input
                value={c.label}
                placeholder="Column label"
                onChange={(e) =>
                  onChange(columns.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                }
                className={'flex-1 ' + cellClass}
              />
              <select
                value={c.cell}
                onChange={(e) =>
                  onChange(
                    columns.map((x, j) =>
                      j === i ? { ...x, cell: e.target.value as GridColumn['cell'] } : x,
                    ),
                  )
                }
                className={cellClass}
              >
                {GRID_CELL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {c.cell === 'select' ? (
                <button
                  type="button"
                  onClick={() => setOpenCol(openCol === i ? null : i)}
                  className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface"
                  aria-label="Edit options"
                >
                  {openCol === i ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onChange(columns.filter((_, j) => j !== i))}
                className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface hover:text-danger"
                aria-label="Remove column"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {c.cell === 'select' && openCol === i ? (
              <div className="mt-2 border-t border-line/10 pt-2">
                <OptionsEditor
                  options={c.options ?? []}
                  onChange={(opts) =>
                    onChange(columns.map((x, j) => (j === i ? { ...x, options: opts } : x)))
                  }
                />
              </div>
            ) : null}
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange([
              ...columns,
              {
                key: `col_${columns.length + 1}`,
                label: `Column ${columns.length + 1}`,
                cell: 'text',
              },
            ])
          }
        >
          Add column
        </Button>
      </div>
    </div>
  );
}
